import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

/**
 * Confirm dialog for a store action, optionally capturing a reason. Used for
 * every irreversible/moderation action (suspend, unpublish, review hold, …).
 */
export const ReasonDialog: React.FC<{
  open: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  destructive?: boolean;
  reasonRequired?: boolean;
  reasonLabel?: string;
  loading?: boolean;
  /** Extra form fields shown above the reason (e.g. a stock adjustment). */
  children?: React.ReactNode;
  /** Keep confirm disabled while the extra fields are invalid. */
  confirmDisabled?: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}> = ({
  open,
  title,
  description,
  confirmLabel,
  destructive,
  reasonRequired,
  reasonLabel = 'Reason',
  loading,
  children,
  confirmDisabled,
  onConfirm,
  onClose,
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const canConfirm = !loading && !confirmDisabled && (!reasonRequired || reason.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {children}

        <div className="space-y-2">
          <label htmlFor="reason" className="text-sm font-medium text-gray-700">
            {reasonLabel}
            {reasonRequired && <span className="text-red-500"> *</span>}
          </label>
          <textarea
            id="reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={reasonRequired ? 'Required — shown to the seller and logged' : 'Optional'}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            onClick={() => onConfirm(reason.trim())}
            disabled={!canConfirm}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
